<template>
    <Modal
        id="create-listing-modal"
        ref="create-listing-modal"
        v-model:open="open"
        title="create_listing"
        width="xl"
        @cancel="cancel"
    >
        <SelectField
            v-model="modelValue.seller_id"
            :options="accounts"
            name="account_title"
            value="account_uid"
            label="account_title"
            :required="true"
            :on-search="loadSelectAccountOptions"
            @change="getQuantityForAccount"
        />
        <SelectField
            v-model="modelValue.asset_id"
            name="asset_title"
            value="asset_uid"
            label="asset_title"
            :options="assets"
            :required="true"
        />
        <NumberField
            v-model="modelValue.quantity"
            :label="$t('quantity')"
            name="quantity"
            :required="true"
            :min="1"
        />
        <NumberField
            v-model="modelValue.price_per_unit"
            :label="$t('price_per_unit')"
            name="price_per_unit"
            :required="true"
            :min="0"
        />
        <TextField
            v-model="modelValue.note"
            :label="$t('note')"
            name="note"
            :rows="3"
        />
        <template #actions>
            <button
                class="btn bg-primary-accent-500 text-white"
                @click="submit"
            >
                {{ $t('save') }}
            </button>
        </template>
    </Modal>
</template>
<script setup lang="ts">
import { Modal, NumberField, SelectField, TextField } from '@src/components'
import { Account, Listing } from '@src/types'
import { useVModels } from '@vueuse/core'
import { onMounted, ref } from 'vue'
import { getPreEmptiveRights, getSmAssets } from '@utils/api/secondaryMarket'
import { getAccounts } from '@utils/api/accounts'

const props = defineProps<{
    open: boolean
    modelValue: Listing
    platformName?: string | null
}>()

const accounts = ref<{ label: string | null; value: number | string | null }[]>(
    []
)
const assets = ref<{ label: string; value: string | null }[]>([])

const emit = defineEmits([
    'update:modelValue',
    'update:open',
    'cancel',
    'submit',
])

const { modelValue, open } = useVModels(props, emit)

function cancel() {
    emit('cancel')
}

function submit() {
    emit('submit')
}

// Use platformName prop directly in the account search API request
async function loadSelectAccountOptions(search: string) {
    console.log('props.platformName', props.platformName)

    const accountsSearchRequest = await getAccounts({
        filter: {
            search: search,
            platform_name_exact: props.platformName,
        },
    })

    accounts.value = accountsSearchRequest.data.accounts.map(
        (account: Account) => {
            return {
                label: account.account_title,
                value: account.account_uid as string,
            }
        }
    )
}

// Pass platformName to getPreEmptiveRights filters as well
async function getQuantityForAccount() {
    if (!modelValue.value?.seller_id) {
        modelValue.value.quantity = 0
        return
    }

    try {
        const response = await getPreEmptiveRights({
            filter: {
                account_uid: modelValue.value.seller_id,
                asset_uid: modelValue.value.asset_id,
                ...(props.platformName
                    ? { platform_name_exact: props.platformName }
                    : {}),
            },
        })

        const items = response?.data?.data || response?.data || []

        const matchingItem = items.find(
            (item: any) => item.account_uid === modelValue.value.seller_id
        )

        const x = matchingItem ? matchingItem.quantity : 0
    } catch (error) {
        console.error('Failed to fetch pre-emptive rights quantity:', error)
        modelValue.value.quantity = 0
    }
}

onMounted(async () => {
    if (props.platformName) {
        const assetsRequest = await getSmAssets(props.platformName)
        console.log('assetsRequest', assetsRequest)
        assets.value = assetsRequest.data.data.assets
    }
})
</script>
